import React, { useState, useCallback } from 'react';
import WarningIcon from '@atlaskit/icon/core/migration/warning';
import type { TriggerProps } from '@atlaskit/popup';
import { token } from '@atlaskit/tokens';
import { IconButton } from '@atlaskit/button/new';
import { JiraPopup as Popup } from '@atlassian/jira-popup/src/ui/jira-popup.tsx';

import { useIntl } from '@atlassian/jira-intl';
import type { Warning } from '@atlassian/jira-portfolio-3-portfolio/src/common/warning-details/types.tsx';
import messages from './messages.tsx';
import { WarningContent } from './warning-content/WarningContent.tsx';

export type Props = {
	warnings?: Warning[];
	isOpen?: boolean;
	zIndex?: number;
	openIssueModal: (warning: Warning) => void;
	openTransitionScreenModal: (warning: Warning) => void;
};

export const WarningButton = (props: Props) => {
	const {
		warnings,
		isOpen: defaultIsOpen,
		zIndex,
		openIssueModal,
		openTransitionScreenModal,
	} = props;
	const [isOpen, setIsOpen] = useState(!!defaultIsOpen);
	const { formatMessage } = useIntl();

	const onClose = useCallback(() => {
		setIsOpen(false);
	}, []);

	const renderContent = useCallback(
		() => (
			<WarningContent
				warnings={warnings}
				setIsOpen={setIsOpen}
				openIssueModal={openIssueModal}
				openTransitionScreenModal={openTransitionScreenModal}
			/>
		),
		[openIssueModal, openTransitionScreenModal, warnings],
	);

	const renderTrigger = useCallback(
		({ ...triggerProps }: TriggerProps) => (
			<IconButton
				{...triggerProps}
				icon={(iconProps) => <WarningIcon {...iconProps} color={token('color.icon.warning')} />}
				appearance="subtle"
				spacing="compact"
				label={formatMessage(messages.warningsButtonLabel)}
				onClick={() => setIsOpen(!isOpen)}
				isSelected={isOpen}
			/>
		),
		[formatMessage, isOpen],
	);

	return (
		<Popup
			messageId="portfolio-3-portfolio.app-simple-plans.top.title-bar.update-jira.changes-table.body-row.warning-button.popup.button.popup"
			messageType="transactional"
			zIndex={zIndex}
			isOpen={isOpen}
			onClose={onClose}
			trigger={renderTrigger}
			content={renderContent}
			placement="bottom-start"
			shouldUseCaptureOnOutsideClick
		/>
	);
};
